import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicHeader from "@/components/layout/PublicHeader";
import { useT, NoTranslate } from "@/lib/i18n";
import HomepageSmartSearch from "@/components/HomepageSmartSearch";
import SmartPersonalizer from "@/components/SmartPersonalizer";
import { usePersonalization, pickHeroSubject, pickHeroSubhead, pickPrimaryCta, pickAudienceEyebrow, pickAudienceSubhead, pickAudienceCta, pickCopilotHeadline, pickCopilotSubhead } from "@/lib/personalizer";
import { trackWithPersonalization } from "@/lib/analytics";
import SmartChatBubble from "@/components/SmartChatBubble";
import SmartLeadMagnet from "@/components/SmartLeadMagnet";
import NewsletterSignup from "@/components/NewsletterSignup";
import FreeToolsGrid from "@/components/landing/FreeToolsGrid";
import ResourcesHub from "@/components/landing/ResourcesHub";
import TestimonialsWall from "@/components/TestimonialsWall";
import TrustBadges from "@/components/TrustBadges";
import FeaturedInBar from "@/components/FeaturedInBar";
import CommunityLeaderboardWidget from "@/components/CommunityLeaderboardWidget";
import { BRAND_LOGO_URL } from "@/components/Brand";
import ProductSchema from "@/components/seo/ProductSchema";
import { usePageTitle } from "@/lib/seo";
import {
  Building2, FileText, ShieldCheck, Wrench, CreditCard, BarChart3,
  ArrowRight, Check, MapPin, Star, Users, Linkedin, Quote, BookOpen,
  ChevronLeft, ChevronRight,
  PhoneCall, Phone, Mail, MessageSquare, Calendar, Receipt, ClipboardList,
  Database, Headphones, AlertOctagon, CheckCircle2, X,
  GraduationCap, Scale, Hammer, Calculator, TrendingUp, Lightbulb, HelpCircle, PlayCircle, Home, Briefcase
} from "lucide-react";
import { CenteredCarousel } from "@/components/CenteredCarousel";
import TestimonialsCarousel from "@/components/landing/TestimonialsCarousel";
import SmartTeamCarousel from "@/components/landing/SmartTeamCarousel";
import InvestorReportsSection from "@/components/landing/InvestorReportsSection";
import DiyResourcesSection from "@/components/landing/DiyResourcesSection";
import ProvinceSoftwareGrid from "@/components/landing/ProvinceSoftwareGrid";
import BlogTeaser from "@/components/landing/BlogTeaser";
import FeatureComparisonMatrix from "@/components/landing/FeatureComparisonMatrix";
import SoftwareAppSchema from "@/components/seo/SoftwareAppSchema";
import ProvincialLawsTeaser from "@/components/landing/ProvincialLawsTeaser";
import UniversityTeaser from "@/components/landing/UniversityTeaser";
import HeroEmailStart from "@/components/landing/HeroEmailStart";
import RentFastSection from "@/components/landing/RentFastSection";
import PortfolioRoiTool from "@/components/landing/PortfolioRoiTool";
import BlogFeedStrip from "@/components/landing/BlogFeedStrip";
import DailySeoRotator from "@/components/seo/DailySeoRotator";
import ExitIntentOffer from "@/components/ExitIntentOffer";
import SandboxDemoHero from "@/components/landing/SandboxDemoHero";
import GuidesHub from "@/components/landing/GuidesHub";
import GetStartedQuiz from "@/components/landing/GetStartedQuiz";
import PocketpillsHome from "@/components/landing/PocketpillsHome";
import { getDailyHeadline } from "@/lib/heroRotation";
import SmartModeSearch from "@/components/SmartModeSearch";
import { Stat, Badge } from "@/components/landing/shared";
import { LANDING_PROVINCES, SOFTWARE_SLUG } from "@/data/landing";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

// Sun-drenched modern Canadian apartment interior — warmer and more
// aspirational than the previous minimalist white render. Used in the
// "For tenants too" section to suggest a renter actually wants to live
// (and use the portal) inside a place like this.
const PROPERTY_IMG = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80";

const features = [
  { icon: Building2, key: "properties", title: "Properties & units", text: "See every unit, vacancy, and rent payment in one place." },
  { icon: FileText, key: "lease", title: "Canadian leases", text: "Create leases for your province, sign them online, and keep them safe." },
  { icon: CreditCard, key: "rent", title: "Rent collection", text: "Collect rent in CAD, apply late fees, and send receipts automatically." },
  { icon: Wrench, key: "maint", title: "Maintenance", text: "Tenants send tickets with photos. You dispatch a vendor in one click." },
  { icon: ShieldCheck, key: "screening", title: "Tenant screening", text: "Run credit and background checks through Certn — results in minutes." },
  { icon: BarChart3, key: "reports", title: "Reports & tax", text: "Track cash flow and export T776, NR4, and T5013 for your accountant." },
];

const plans = [
  {
    key: "starter",
    name: "Starter",
    price: 60,
    target: "1–10 units",
    tagline: "For landlords with 1–10 units.",
    features: [
      "Rent collection in CAD",
      "Lease uploads and e-signature",
      "Automatic listing to Facebook and Kijiji",
      "Expense tracking",
      "Tenant applications and smart-lock vendor PINs",
      "Maintenance tickets with photos",
    ],
    highlight: false,
  },
  {
    key: "professional",
    name: "Growth / Professional",
    price: 150,
    target: "20–100 units",
    tagline: "For growing portfolios with better reports.",
    features: [
      "Everything in Starter",
      "Unlimited e-signatures",
      "Automatic late-fee handling",
      "Plain-English ledger you can query",
      "Move-out risk alerts and retention actions",
      "Photo-based property condition reports",
    ],
    highlight: true,
  },
  {
    key: "enterprise",
    name: "Enterprise",
    price: 250,
    target: "100+ units",
    tagline: "For multi-property portfolios and teams.",
    features: [
      "Everything in Growth",
      "Split payments for brokerages (no trust account needed)",
      "Automated workflows for your team",
      "Alerts for duplicate bills and unusual expenses",
      "Portfolio benchmarking against CMHC and CRA data",
      "Bookkeeping help, CRA tax Q&A, priority Canadian support",
    ],
    highlight: false,
  },
];

const testimonials = [
  {
    metric: "18 → 4 hours",
    metric_label: "Weekly hours on rent collection",
    quote: "Rent day used to eat 18 hours a month across 14 doors. After switching to Central Rentals, it's under 4 — Stripe auto-collects, receipts send themselves, and I close the books on the 1st instead of the 5th. That reclaimed 14 hours went straight into acquiring 3 more units.",
    name: "Kevin M.",
    detail: "14 units · Ottawa, ON",
    result: "Reclaimed 14 hours/month + added 3 units",
  },
  {
    metric: "$4,200",
    metric_label: "Paralegal fees avoided (year 1)",
    quote: "My paralegal used to charge $350 per lease. With the Ontario Standard Lease generator + custom clauses + e-signature, I've done 12 leases this year in-app. $4,200 saved, and my paralegal still reviews the trickier ones — she said the output is cleaner than most drafts she sees.",
    name: "Priya S.",
    detail: "6 units · Mississauga, ON",
    result: "Cut lease-drafting costs by 88%",
  },
  {
    metric: "3 evictions",
    metric_label: "Caught before signing",
    quote: "The Equifax-backed screening report flagged an applicant with three prior LTB evictions across two provinces — none showed on their pay stubs or references. That one report saved me an estimated $22,000 in lost rent + legal fees. Screening alone paid for the subscription for 30+ years.",
    name: "David L.",
    detail: "3 units · Calgary, AB",
    result: "$22K downside prevented in one screening",
  },
  {
    metric: "T776 in 45 min",
    metric_label: "Was 3 evenings prior year",
    quote: "Tax season used to be a shoebox of receipts and three evenings of Excel. Now I click Export → T776 and my accountant has a CRA-ready file in 45 minutes. She dropped my tax-prep fee by $180 because her side of the work fell off a cliff.",
    name: "Aisha R.",
    detail: "3 units · Halifax, NS",
    result: "$180 accountant discount + evenings back",
  },
  {
    metric: "62% faster",
    metric_label: "Maintenance ticket resolution",
    quote: "Tenants used to text me at 11pm with 'the sink is broken' and no photos. Now every ticket comes in with photos, severity, and the vendor auto-dispatched. Average time-to-fix went from 4.1 days to 1.6. My 5-star tenant reviews doubled in a quarter.",
    name: "Jasmine T.",
    detail: "9 units · Surrey, BC",
    result: "Resolution time 4.1d → 1.6d, reviews 2×",
  },
  {
    metric: "Vacancy 8% → 2%",
    metric_label: "Portfolio-wide (12 months)",
    quote: "I switched from a US platform that didn't know what an N4 was. Central Rentals auto-lists to Kijiji + Facebook + our syndication network the moment a tenant gives notice, and the applicant funnel now delivers a signed lease in 11 days instead of 34. Vacancy dropped from 8% to 2% portfolio-wide.",
    name: "Marc L.",
    detail: "8 units · Gatineau, QC",
    result: "6-point vacancy drop = ~$14K/year retained",
  },
];

const faqs = [
  {
    q: "What is Central Rentals landlord software?",
    a: "Central Rentals is an all-in-one property management platform built for Canadian landlords. It combines rent collection, tenant screening, Canadian-compliant lease generation, maintenance ticketing, document storage, and tax-ready reporting (T776, NR4, T5013) in a single dashboard — for portfolios from 1 to 1,000+ units.",
  },
  {
    q: "How much does it cost?",
    a: "Pricing lives inside the Landlord portal. Sign up free and choose a plan from Settings → Billing once you're inside — cancel any time.",
  },
  {
    q: "How do tenants pay rent?",
    a: "Tenants pay online in CAD through their own tenant portal, powered by Stripe. Rent invoices go out automatically, late fees can be applied automatically, and both you and your tenant receive instant receipts. We never see or store card details — payments are PCI-DSS compliant via Stripe.",
  },
  {
    q: "Are the leases legally compliant in my province?",
    a: "Yes. The smart lease generator produces leases tailored to all 13 provinces and territories, including the Ontario Standard Lease and provincial notice forms (N1–N13). You can add your own custom clauses, send for e-signature, and store the signed copy permanently in your document vault.",
  },
  {
    q: "How does tenant screening work?",
    a: "Applicants complete a digital application, and you can run background and credit checks through our screening partner Certn. Results — credit score, eviction history, and identity verification — arrive in your dashboard so you can approve or decline with confidence.",
  },
  {
    q: "Can it help me at tax time?",
    a: "Absolutely. Every rent payment and expense is tracked all year, and you can export T776-ready income statements, NR4 summaries for non-resident owners, and T5013 reports for investor partnerships — ready to hand to your accountant.",
  },
  {
    q: "What about maintenance requests?",
    a: "Tenants submit tickets with photos directly from their portal. You triage from your dashboard, dispatch to your preferred vendor, and everyone tracks status in real time — no more midnight phone calls or lost text threads.",
  },
  {
    q: "How do I get started?",
    a: "Sign up free in about 30 seconds — cancel any time. Add your first property, invite your tenants, and you can be collecting rent online the same day. Cancel anytime.",
  },
];

// One slide = one testimonial. On mobile the carousel shows a single card,
// on md+ it shows two side-by-side, lg+ shows three — sliding by one card
// per step so every quote rotates through center over time.
// Extracted to /components/landing/TestimonialsCarousel.jsx in the refactor.

// "Smart team" = the visible roster of automated employees that handle
// landlord work 24/7. Each card pairs an icon, a job title, a one-line
// description and the call-to-action route inside the app.
const SMART_TEAM = [
  { icon: PhoneCall,     title: "Front Desk",            tagline: "Answers calls, voicemails & SMS",
    body: "Greets renters, books showings, and routes urgent calls to a human only when it has to.",
    cta: "/smart?tab=conversations", color: "from-emerald-500 to-teal-500" },
  { icon: Mail,          title: "Inbox Operator",        tagline: "Email + chat + voice in one thread",
    body: "Unified omnichannel inbox — tenants get one consistent answer, you never repeat yourself.",
    cta: "/smart?tab=conversations", color: "from-sky-500 to-indigo-500" },
  { icon: Wrench,        title: "Smart Maintenance",     tagline: "Severity-graded ticket triage",
    body: "Reads tenant photos & messages, assigns severity, drafts a reply, and routes to the right pro.",
    cta: "/smart?tab=maintenance", color: "from-amber-500 to-orange-500" },
  { icon: Database,      title: "MLS Sync",              tagline: "Listings everywhere, automatically",
    body: "Two-way sync to MLS, Kijiji, Facebook & Realtor.ca — one edit updates everywhere.",
    cta: "/smart?tab=leasing", color: "from-violet-500 to-purple-500" },
  { icon: Calendar,      title: "Calendar & Ops",        tagline: "Inspections, renewals, growth tasks",
    body: "Schedules inspections, lease renewals, open houses and your personal life — one calendar.",
    cta: "/smart?tab=conversations", color: "from-rose-500 to-pink-500" },
  { icon: Headphones,    title: "Dedicated PM",          tagline: "Quality + escalation backstop",
    body: "A real human property manager validates the work, catches misses, and reports weekly.",
    cta: "/contact", color: "from-emerald-600 to-cyan-600" },
  { icon: AlertOctagon,  title: "24/7 Urgent Desk",      tagline: "Floods, fires, lockouts handled",
    body: "Even at 3am, urgent tasks get triaged, vendors dispatched, and you get a clean morning brief.",
    cta: "/smart?tab=maintenance", color: "from-red-500 to-rose-500" },
  { icon: ClipboardList, title: "Support Ops",           tagline: "Tickets · demos · docs",
    body: "Handles support tickets, schedules product demos, updates project boards, and keeps docs tidy.",
    cta: "/smart?tab=conversations", color: "from-indigo-500 to-blue-500" },
  { icon: Receipt,       title: "AR / AP Bookkeeper",    tagline: "Invoices · expenses · reconciliations",
    body: "Tracks invoices, reconciles bank accounts, files expense reports, follows up on AR, books appraisals.",
    cta: "/smart?tab=ledger", color: "from-teal-500 to-emerald-500" },
  { icon: FileText,      title: "Tax Workpapers",        tagline: "T776, NR4 & extensions",
    body: "Organizes workpapers, sends document requests, manages extension filings, tracks CRA deadlines.",
    cta: "/smart?tab=ledger", color: "from-slate-700 to-slate-900" },
];

function SmartTeamCarousel_REMOVED() { return null; }
// Component moved to /components/landing/SmartTeamCarousel.jsx

function InvestorReportsSection_REMOVED() { return null; }
function RequestReportModal_REMOVED() { return null; }
// Components moved to /components/landing/InvestorReportsSection.jsx


// 13 provinces — kept inline so the homepage doesn't import LandlordLaws.jsx
// (which would pull in heavy LAWS data on every Landing render).
const LANDING_PROVINCES_LOCAL_NOTE = null; // moved to /data/landing.js — kept import alias above

// DiyResourcesSection extracted to /components/landing/DiyResourcesSection.jsx

function DiyResourcesSection_REMOVED() { return null; }
// Component moved to /components/landing/DiyResourcesSection.jsx

function ProvincialLawsTeaser_REMOVED() { return null; }
// Component moved to /components/landing/ProvincialLawsTeaser.jsx

// SEO-focused internal link hub — every province software page is one
// click from the homepage so Google can crawl them and PageRank flows
// in. Also the canonical entry point for users searching
// "property management software in [province]".
function ProvinceSoftwareGrid_REMOVED() { return null; }
// Component moved to /components/landing/ProvinceSoftwareGrid.jsx

function UniversityTeaser_REMOVED() { return null; }
// Component moved to /components/landing/UniversityTeaser.jsx

const HELP_CATEGORIES = [
  { id: "billing",     label: "Billing & subscriptions" },
  { id: "technical",   label: "Technical issue" },
  { id: "feature",     label: "Feature request" },
  { id: "onboarding",  label: "Onboarding help" },
  { id: "compliance",  label: "Compliance / RTA question" },
  { id: "partnership", label: "Partnership / press" },
  { id: "other",       label: "Something else" },
];

function HelpCentreSection() {
  const [form, setForm] = useState({ name: "", email: "", category: "billing", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    const name = form.name.trim(), email = form.email.trim();
    const subject = form.subject.trim(), message = form.message.trim();
    if (!name || !email || !subject || !message) { setErr("All fields are required."); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api/help-centre/request`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, category: form.category, subject, message }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Request failed");
      setDone(true);
    } catch (e) {
      setErr(e.message || "Something went wrong.");
    } finally { setSubmitting(false); }
  };

  return (
    <section id="help-centre" className="py-6 md:py-8 bg-slate-950 text-white">
      <div className="max-w-5xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-300">
            <HelpCircle className="w-3 h-3"/> Help centre
          </div>
          <h2 className="font-display mt-4 text-2xl sm:text-3xl md:text-4xl font-bold tracking-tighter leading-[1.05]">
            Stuck on something? Tell us.
          </h2>
          <p className="mt-4 text-base text-slate-300 leading-relaxed max-w-md">
            We&apos;ll get back to you within <b className="text-white">1 business day</b>. Use this for billing, technical bugs, feature requests, onboarding help and partnerships.
          </p>
          <div className="mt-6 text-sm text-slate-400 space-y-2">
            <div className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400"/> Reply within 1 business day</div>
            <div className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400"/> Auto-confirmation to your inbox</div>
            <div className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400"/> Stored against your account if signed in</div>
          </div>
        </div>

        <div className="bg-white rounded-3xl p-7 md:p-8 text-slate-900 shadow-2xl shadow-emerald-900/20">
          {done ? (
            <div className="text-center py-8" data-testid="help-centre-done">
              <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-600"/>
              </div>
              <h3 className="font-display text-xl font-extrabold mt-4 text-slate-950">Got it — message sent</h3>
              <p className="text-sm text-slate-600 mt-2 max-w-sm mx-auto">A confirmation was emailed to <b className="text-slate-900">{form.email}</b>. We&apos;ll reply within 1 business day.</p>
              <button type="button" onClick={() => { setDone(false); setForm({ name: "", email: "", category: "billing", subject: "", message: "" }); }}
                data-testid="help-centre-new"
                className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-emerald-700 hover:text-emerald-800 hover:underline">
                Send another request
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="help-centre-form">
              <h3 className="font-display text-xl font-extrabold text-slate-950">Submit a request</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-1 block">Name *</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="help-centre-name"
                    className="w-full border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 outline-none"/>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-1 block">Email *</label>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="help-centre-email" placeholder="you@example.com"
                    className="w-full border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 outline-none"/>
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-1 block">Category *</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  data-testid="help-centre-category"
                  className="w-full border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 outline-none">
                  {HELP_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-1 block">Subject *</label>
                <input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  data-testid="help-centre-subject" placeholder="One-line summary"
                  className="w-full border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 outline-none"/>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-1 block">Message *</label>
                <textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="help-centre-message" placeholder="What's going on? More detail = faster reply."
                  className="w-full border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 outline-none resize-none"/>
              </div>
              {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="help-centre-error">{err}</div>}
              <button type="submit" disabled={submitting} data-testid="help-centre-submit"
                style={{
                  display: "flex",
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  padding: "16px 20px",
                  borderRadius: "9999px",
                  backgroundColor: "#059669",
                  color: "#ffffff",
                  fontWeight: 800,
                  fontSize: "16px",
                  border: "0",
                  cursor: submitting ? "not-allowed" : "pointer",
                  opacity: submitting ? 0.6 : 1,
                  boxShadow: "0 10px 25px -5px rgba(5,150,105,0.4)",
                  transition: "background-color 0.2s ease",
                }}
                onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.backgroundColor = "#047857"; }}
                onMouseLeave={(e) => { if (!submitting) e.currentTarget.style.backgroundColor = "#059669"; }}>
                {submitting ? "Sending…" : (<>Submit request <ArrowRight className="w-5 h-5"/></>)}
              </button>
              <p className="text-[11px] text-slate-500 leading-relaxed">By submitting you consent to be contacted about your request. We won&apos;t add you to any newsletter — you control that separately.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}



export default function Landing() {
  const t = useT();
  usePageTitle(
    "Property Management Software for Small Landlords in Canada — Central Rentals",
    "The property management software Canadian small landlords actually finish setup on. Rent tracking, N4/N12 & provincial notices, T776 exports, tenant screening — built in Canada for landlords with 1–20 units. Free to start."
  );
  // Smart Personalization Engine — Duda-style dynamic content swap based
  // on province (timezone), referrer, device, and visitor history. All
  // detection is client-side, zero PII, zero network round-trip.
  const personal = usePersonalization();
  const heroSubject = pickHeroSubject(personal);
  // Audience-keyed hero copy: PMC sees competitive enterprise messaging,
  // individual landlords see PLG-style messaging, tenants see tenant-side
  // CTAs. Falls back to referrer-based subhead for "unknown" audience.
  const audienceSubhead = personal.audience !== "unknown" ? pickAudienceSubhead(personal) : pickHeroSubhead(personal);
  const audienceEyebrow = pickAudienceEyebrow(personal);
  const audienceCta = pickAudienceCta(personal);
  const heroSubhead = audienceSubhead;
  const primaryCta  = pickPrimaryCta(personal);
  const copilotHeadline = pickCopilotHeadline(personal);
  const copilotSubhead = pickCopilotSubhead(personal);

  // Fire page_view + variant_render so the analytics dashboard can rank
  // referrer / visitor_type combos by conversion rate. Re-fires when the
  // personalization snapshot actually changes (e.g., after visit-count bump).
  useEffect(() => {
    // Page views are recorded once by the global route tracker.
    trackWithPersonalization("variant_render", personal, { variant: primaryCta.label });
  }, [personal.referrer, personal.visitor, personal.device, personal.audience, primaryCta.label]);

  // Tenant Referral capture — when a landlord lands with ?rref=CODE we
  // store the code in localStorage and ping the backend for attribution.
  // The signup flow will read the code and call /signup-claim on success.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const rref = params.get("rref");
      if (rref) {
        localStorage.setItem("crc_rref", rref.toUpperCase());
        const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
        fetch(`${API}/api/tenant-referral/landing-attribute`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: rref, kind: "landing" }),
        }).catch(() => { /* silent */ });
      }
    } catch { /* noop */ }
  }, []);
  return (
    <div className="bg-white text-slate-900 landing-page" data-testid="landing-root">
      <SoftwareAppSchema />
      {/* FAQPage schema — grounds Perplexity/ChatGPT/Claude/Gemini answers
          against our authoritative Q&A rather than random third parties. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "@id": "https://centralrentals.ca/#faq",
            mainEntity: faqs.slice(0, 10).map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
      {/* Solid white header strip so the logo never sits on top of the
          animated aurora gradient. */}
      <div className="relative z-20 bg-white border-b border-slate-100">
        <PublicHeader light={true} />
        <ProductSchema />
        <SmartPersonalizer />
      </div>

      {/* HERO — Pocketpills-style rebuild (2026-02): rounded pastel emerald
          hero card, chip trust row, portrait-in-blob eligibility card,
          pill pricing cards, checklist card, dark rounded CTA hero,
          quick-start icon grid, and provincial coverage list. All old
          hero/trust-strip/features/tenant sections replaced. */}
      <PocketpillsHome />
      {/* Legacy hero kept below inside a hidden wrapper so any deep-link
          anchors (#sandbox-demo, #features, #tenants) still resolve. */}
      <div className="hidden">
      <div className="relative min-h-[60vh] overflow-hidden text-slate-900 brand-hero">
        <div className="brand-hero-dots"></div>
        <div className="brand-hero-sweep"></div>
        <div className="brand-hero-grain"></div>
        <div className="relative z-10">
          <section className="max-w-7xl mx-auto px-5 md:px-8 pt-6 md:pt-10 pb-8 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7 min-w-0 animate-fade-in-up text-center lg:text-left mx-auto lg:mx-0 w-full max-w-2xl lg:max-w-none flex flex-col items-center lg:items-start">
              {(() => { const daily = getDailyHeadline(); return (
              <>
              <div className="inline-flex items-center gap-2 mb-7 px-3 py-1.5 rounded-full bg-white/80 border border-slate-200 backdrop-blur-md text-xs font-semibold tracking-wide text-slate-700 shadow-sm" data-testid="hero-audience-chip">
                <MapPin className="w-3.5 h-3.5 text-emerald-600"/> {daily.eyebrow}
              </div>
              <h1 className="font-display mt-6 text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold leading-[1.05] tracking-tighter text-slate-900 break-words" data-testid="hero-h1">
                {daily.line1}<br/>
                <span className="bg-gradient-to-r from-emerald-600 via-teal-500 to-sky-500 bg-clip-text text-transparent">
                  {daily.gradient}
                </span>
              </h1>
              </>
              );})()}
              <p className="mt-7 text-lg md:text-xl text-slate-600 max-w-2xl mx-auto lg:mx-0 leading-relaxed" data-testid="hero-subhead">
                {t("landing.hero_subtitle", "Central Rentals collects it for you — in Canadian dollars, PIPEDA-compliant, cancel any time.")}
              </p>
              <div className="mt-9 w-full min-w-0 max-w-lg mx-auto lg:mx-0" data-testid="hero-cta-block">
                {personal.audience === "pmc" || personal.audience === "tenant" ? (
                  <Link
                    to={audienceCta.to}
                    data-testid={audienceCta.testid}
                    className={`inline-flex items-center gap-2 px-6 py-4 rounded-2xl font-bold shadow-lg transition-all ${
                      personal.audience === "tenant"
                        ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-600/30"
                        : "bg-slate-900 hover:bg-slate-800 text-white shadow-slate-900/30"
                    }`}
                  >
                    {audienceCta.label} <ArrowRight className="w-4 h-4"/>
                  </Link>
                ) : (
                  <GetStartedQuiz />
                )}
                <div className="mt-4 text-xs text-slate-600 text-center lg:text-left">
                  {t("landing.already_have_account", "Already have an account?")}{" "}
                  <Link to="/login/landlord" data-testid="hero-cta-login-landlord"
                    className="font-bold text-slate-800 hover:text-emerald-700 underline-offset-2 hover:underline">{t("landing.cta_login_ll", "Landlord login")}</Link>
                  <span className="mx-2 text-slate-400">·</span>
                  <Link to="/login/tenant" data-testid="hero-cta-login-tenant"
                    className="font-bold text-slate-800 hover:text-emerald-700 underline-offset-2 hover:underline">{t("landing.cta_login_tn", "Tenant login")}</Link>
                </div>
                {/* Above-fold secondary CTA: interactive calculator + product tour.
                    Two visually distinct, equally-sized cards so neither steals
                    focus from the primary "Choose a plan" CTA above. */}
                <div className="mt-6 grid grid-cols-2 gap-3 max-w-md mx-auto lg:mx-0" data-testid="hero-calc-ctas">
                  <Link
                    to="/tools/roi-calculator"
                    data-testid="hero-roi-calc-cta"
                    className="group relative flex items-center gap-3 p-3.5 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-500 hover:border-emerald-600 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                  >
                    <div className="shrink-0 w-9 h-9 rounded-xl bg-emerald-600 group-hover:bg-emerald-700 text-white flex items-center justify-center transition-colors">
                      <Calculator className="w-5 h-5"/>
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold leading-none">Free tool</div>
                      <div className="text-sm font-extrabold text-slate-900 mt-1 leading-tight">{t("landing.calculate_roi", "Calculate ROI")}</div>
                    </div>
                  </Link>
                  <Link
                    to="/#sandbox-demo"
                    onClick={(e) => {
                      // Auto-scroll to the live Copilot demo and briefly
                      // flash a "play mode" ring so visitors see the
                      // sandbox is running. The demo itself already
                      // autoplays (typewriter cycles CONVO on mount).
                      const el = document.getElementById("sandbox-demo");
                      if (el) {
                        e.preventDefault();
                        el.scrollIntoView({ behavior: "smooth", block: "start" });
                        el.classList.add("demo-play-mode");
                        window.setTimeout(() => el.classList.remove("demo-play-mode"), 2600);
                      }
                    }}
                    data-testid="hero-see-in-action-cta"
                    className="group relative flex items-center gap-3 p-3.5 rounded-2xl bg-white border-2 border-slate-200 hover:border-slate-900 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
                  >
                    <div className="shrink-0 w-9 h-9 rounded-xl bg-slate-900 group-hover:bg-slate-800 text-white flex items-center justify-center transition-colors">
                      <PlayCircle className="w-5 h-5"/>
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold leading-none">2-min demo</div>
                      <div className="text-sm font-extrabold text-slate-900 mt-1 leading-tight">{t("landing.see_in_action", "See it in action")}</div>
                    </div>
                  </Link>
                </div>
              </div>
              <div className="mt-10 flex flex-wrap items-center justify-center lg:justify-start gap-x-7 gap-y-3 text-sm text-slate-600" data-testid="hero-social-proof">
                <div className="flex items-center gap-2"><Users className="w-4 h-4 text-emerald-600"/> {t("landing.trust_line", "Built by Canadian landlords, for Canadian landlords")}</div>
                <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-emerald-600"/> {t("landing.provinces_terr", "All 13 provinces + territories")}</div>
              </div>
              <HomepageSmartSearch />
            </div>
            <div className="lg:col-span-5 flex items-end scroll-mt-24 rounded-2xl" id="sandbox-demo">
              <SandboxDemoHero />
            </div>
          </section>
        </div>
      </div>

      {/* TRUST STRIP */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-5 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <Stat label={t("landing.stat_coverage", "Coverage")} value={t("landing.stat_all_provinces", "All 13 provinces & territories")}/>
          <Stat label={t("landing.stat_notices", "Provincial notices")} value={t("landing.stat_notices_val", "N1–N13 ready")}/>
          <Stat label={t("landing.stat_tax", "Tax exports")} value={t("landing.stat_tax_val", "T776 · NR4 · T5013")}/>
          <Stat label={t("landing.stat_payments", "Payments")} value={t("landing.stat_payments_val", "Stripe · CAD")}/>
        </div>
      </section>

      {/* Legacy feature detail cards — kept below the menu for visitors
          who want a quick visual summary before diving in. */}
      <section id="features" className="py-8 md:py-10 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl">
            <div className="text-xs tracking-[0.22em] uppercase font-bold text-emerald-600">{t("landing.whats_inside", "What's inside")}</div>
            <h2 className="font-display mt-3 text-2xl sm:text-3xl md:text-4xl font-bold tracking-tighter text-slate-900 leading-[1.1]">
              {t("landing.everything_ll", "Everything landlords need. Nothing tenants hate.")}
            </h2>
            <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
              {t("landing.engineered", "Built for Canadian rentals — every province, every notice form, and yes, snow removal too.")}
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f, i) => (
              <div key={i} data-testid={`feature-card-${i}`}
                className="group p-6 rounded-2xl border border-slate-200 bg-white hover:border-emerald-200 hover:shadow-md transition-all">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100 flex items-center justify-center mb-4 group-hover:bg-emerald-100 transition-colors">
                  <f.icon className="w-5 h-5"/>
                </div>
                <div className="font-display font-bold text-lg text-slate-900">{t(`feature.${f.key}_title`, f.title)}</div>
                <p className="mt-1.5 text-base text-slate-600 leading-relaxed">{t(`feature.${f.key}_text`, f.text)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DUAL EXPERIENCE — modern tenant portal card, no photo */}
      <section id="tenants" className="py-8 md:py-12 bg-gradient-to-br from-sky-50 via-white to-emerald-50 border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-100 text-sky-800 border border-sky-200 text-[11px] font-extrabold tracking-[0.22em] uppercase">
            <Home className="w-3.5 h-3.5"/> For tenants too
          </div>
          <h2 className="font-display mt-4 text-2xl sm:text-3xl md:text-4xl font-black tracking-tight text-slate-950 leading-[1.05]">
            A tenant portal your renters will actually use.
          </h2>
          <ul className="mt-6 grid sm:grid-cols-2 gap-2.5 text-left max-w-2xl mx-auto">
            {[
              "One-tap rent payment in CAD",
              "Chat about repairs with photos in real time",
              "Automatic rent receipts to help build credit",
              "Every lease and signed document in one place",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-2.5 text-slate-800 text-[14px] bg-white rounded-lg border border-slate-200 px-3 py-2.5">
                <div className="mt-0.5 w-5 h-5 rounded-full bg-emerald-500/10 text-emerald-600 ring-1 ring-emerald-200 flex items-center justify-center flex-none">
                  <Check className="w-3 h-3"/>
                </div>
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/signup?role=tenant" data-testid="tenant-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 shadow-lg shadow-emerald-500/25">
              Open my tenant portal <ArrowRight className="w-4 h-4"/>
            </Link>
            <Link to="/login/tenant" data-testid="tenant-cta-login"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-slate-200 hover:bg-slate-50 text-slate-800 font-semibold text-sm">
              Tenant sign-in
            </Link>
          </div>
        </div>
      </section>
      </div>{/* end hidden legacy block */}

      {/* RESOURCES HUB — compact 3-persona quick-access grid. Sits
          between the tenant portal pitch and the product deep-dive so
          landlords / property managers / agents who bounce here from
          a Google search find every fast-solution surface in one
          screen. Keeps the fold short — no long marketing scroll. */}
      <section id="resources" className="py-6 md:py-8 bg-white border-b border-slate-200" data-testid="landing-resources">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl">
            <div className="text-xs tracking-[0.22em] uppercase font-bold text-emerald-600">Resources</div>
            <h2 className="font-display mt-3 text-2xl sm:text-3xl md:text-4xl font-bold tracking-tighter text-slate-900 leading-[1.05]">
              Quick answers for landlords, property managers &amp; agents.
            </h2>
            <p className="mt-3 text-slate-600 text-[15px]">Every guide, calculator, and provincial resource we have — one click each.</p>
          </div>

          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { to: "/tools/roi-calculator",              eyebrow: "Landlord",         title: "Rental ROI calculator",           body: "Cap rate, cash flow, break-even — 20 seconds." },
              { to: "/features/tenant-screening-canada",   eyebrow: "Landlord",         title: "Screen a tenant in 90 s",         body: "PIPEDA-compliant. Equifax + references." },
              { to: "/features/provincial-lease-generator",eyebrow: "Property manager", title: "All-13-province lease pack",       body: "Ontario, BC, AB, QC, MB… every mandatory form." },
              { to: "/features/t776-tax-export-software",  eyebrow: "Property manager", title: "One-click T776 CRA export",        body: "Pre-mapped to the 12 CRA categories." },
              { to: "/vs",                                 eyebrow: "Real estate agent",title: "Central Rentals vs Buildium",      body: "Head-to-head with the top 8 competitors." },
              { to: "/integrate",                          eyebrow: "Real estate agent",title: "Stripe, QuickBooks, Zapier + more", body: "Every integration Central Rentals ships." },
              { to: "/signup?role=tenant",                 eyebrow: "Tenant",           title: "Pay rent in CAD in one tap",       body: "Interac, card, or PAD — instant receipts." },
              { to: "/answers",       eyebrow: "Tenant",           title: "Know your rights, by province",    body: "N4, deposits, repairs — plain-English guide." },
            ].map((r, i) => (
              <Link key={i} to={r.to} data-testid={`resource-card-${i}`}
                className="group block p-4 rounded-xl border border-slate-200 bg-white hover:border-emerald-500 hover:shadow-md transition-all">
                <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-emerald-700">{r.eyebrow}</div>
                <div className="mt-1.5 font-bold text-slate-900 text-[15px] leading-snug">{r.title}</div>
                <p className="mt-1 text-xs text-slate-600 leading-relaxed">{r.body}</p>
                <div className="mt-2.5 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 group-hover:text-emerald-800">
                  Open <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT section removed to shorten the page — pricing lives at /pricing. */}

      {/* TESTIMONIALS */}
      {/* Testimonials section hidden until we have real, verified reviews.
          Fake city-landlord quotes were removed per product direction —
          visitors trust honesty over marketing. */}
      {false && (
      <section id="testimonials" className="py-8 md:py-10 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid lg:grid-cols-[1.6fr,1fr] gap-10 lg:gap-12 items-start">
            <div>
              <div className="text-xs tracking-[0.22em] uppercase font-bold text-emerald-600">Testimonials</div>
              <h2 className="font-display mt-3 text-2xl sm:text-3xl md:text-4xl font-bold tracking-tighter text-slate-900 leading-[1.05]">
                What landlords are saying about Central Rentals Canada.
              </h2>
              <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
                From single-unit owners to multi-province portfolios — here&apos;s how Canadian landlords run their rentals with us.
              </p>
            </div>
            <CommunityLeaderboardWidget limit={10} emptyOnly/>
          </div>

          <TestimonialsCarousel testimonials={testimonials} />
        </div>
      </section>
      )}

      {/* "As featured in" — real, verified press placements only.
          Silently hidden until you add real placements via
          POST /api/press/placements. Fabricating outlets would violate
          Canada's Competition Act s. 74.01 + outlet trademarks. */}
      <FeaturedInBar />

      {/* Mark · Smart Operator positioning section removed to shorten the
          page — Mark is now surfaced via the "Ask Mark" chat bubble. */}

      {/* FAQ */}
      <section id="faq" className="py-8 md:py-10 bg-white">
        <details className="max-w-3xl mx-auto px-5 md:px-8 group">
          <summary className="cursor-pointer list-none text-center py-2">
            <div className="text-xs tracking-[0.22em] uppercase font-bold text-emerald-600">FAQs</div>
            <h2 className="font-display mt-2 text-xl sm:text-2xl font-black tracking-tight text-slate-900">
              Landlord software FAQs
              <span className="ml-2 text-sm text-emerald-700 group-open:rotate-180 inline-block transition-transform">⌄</span>
            </h2>
          </summary>
          <div className="pt-4">
          <p className="text-center text-base text-slate-600">
            Everything you need to know before you bring your portfolio online.
          </p>

          <Accordion type="single" collapsible className="mt-6" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-slate-200">
                <AccordionTrigger data-testid={`faq-question-${i}`}
                  className="text-left text-[15px] md:text-base font-semibold text-slate-900 hover:text-emerald-700 hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent data-testid={`faq-answer-${i}`} className="text-[15px] text-slate-600 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-8 text-center text-sm text-slate-500">
            Still have questions? Email us at{" "}
            <a href="mailto:contact@centralrentals.ca" className="text-emerald-700 font-semibold hover:underline">
              contact@centralrentals.ca
            </a>
          </div>
          </div>
        </details>
      </section>

      <BlogTeaser/>
      <FeatureComparisonMatrix/>

      {/* COMPANY — Who We Are, Careers, D&I, Investors */}
      <section id="company" className="py-8 md:py-10 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="text-xs tracking-[0.22em] uppercase font-bold text-emerald-600 text-center">Company</div>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter text-slate-900 text-center mt-2">
            More than software.
          </h2>
          <p className="text-slate-600 text-center mt-4 max-w-2xl mx-auto">
            We&apos;re a Canadian team building for Canadian landlords. Here&apos;s how we hire, what we stand for, and how we grow.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10" data-testid="home-company-tiles">
            {[
              {
                to: "/about",
                eyebrow: "Who we are",
                title: "Built by landlords, for landlords",
                blurb: "Canadian-built, growing across all 13 provinces. Meet the team and read our mission.",
                accent: "from-emerald-100 to-emerald-50",
                color: "text-emerald-700",
                testid: "tile-about",
              },
              {
                to: "/careers",
                eyebrow: "Careers",
                title: "We're hiring",
                blurb: "Help us ship faster than any US property management platform. Engineering, customer success, marketing — all roles posted with salary bands.",
                accent: "from-amber-100 to-amber-50",
                color: "text-amber-700",
                testid: "tile-careers",
              },
              {
                to: "/diversity",
                eyebrow: "Diversity & Inclusion",
                title: "Housing for everyone",
                blurb: "Inclusive hiring, WCAG 2.2 AA accessibility, fair-housing screening guards, and 1% of revenue to Indigenous-led housing.",
                accent: "from-violet-100 to-violet-50",
                color: "text-violet-700",
                testid: "tile-diversity",
              },
              {
                to: "/investors",
                eyebrow: "Investor Relations",
                title: "Canadian rental software",
                blurb: "Read our investor briefing, the case for the Canadian rental OS, and meet the team building it. Quarterly updates available on request.",
                accent: "from-sky-100 to-sky-50",
                color: "text-sky-700",
                testid: "tile-investors",
              },
            ].map(t => (
              <Link key={t.to} to={t.to} data-testid={t.testid}
                className="group rounded-2xl bg-white border border-slate-200 overflow-hidden hover:border-emerald-300 hover:shadow-xl transition-all">
                <div className={`h-2 bg-gradient-to-r ${t.accent}`}/>
                <div className="p-6">
                  <div className={`text-[11px] uppercase tracking-[0.18em] font-bold ${t.color}`}>{t.eyebrow}</div>
                  <div className="font-display text-lg font-extrabold text-slate-900 mt-2">{t.title}</div>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{t.blurb}</p>
                  <div className={`mt-4 inline-flex items-center gap-1 ${t.color} font-semibold text-sm group-hover:gap-2 transition-all`}>
                    Learn more <ArrowRight className="w-3.5 h-3.5"/>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>



      {/* DIY RESOURCES + PROVINCIAL LAWS + UNIVERSITY TEASER + HELP CENTRE */}
      <DiyResourcesSection />
      <RentFastSection />
      <PortfolioRoiTool />
      <BlogFeedStrip />
      <ProvinceSoftwareGrid />
      <DailySeoRotator />
      <ExitIntentOffer />
      <div className="max-w-5xl mx-auto px-5 md:px-8 -mt-6 mb-6">
        <SmartModeSearch />
      </div>
      <GuidesHub />
      <ProvincialLawsTeaser />
      <UniversityTeaser />
      <HelpCentreSection />

      {/* GOVERNMENT & INDUSTRY RESOURCES — Housing, Landlord, Brokerage,
          Property Manager info cards linking to official Canadian
          government sources. Boosts trust + E-E-A-T (author expertise)
          and gives users authoritative reference material they can
          cross-check against Central Rentals' own guides. */}
      <section id="government-resources" className="py-8 md:py-12 bg-gradient-to-br from-white via-emerald-50/40 to-sky-50/40 border-y border-slate-200" data-testid="gov-resources">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 border border-emerald-200 text-[11px] font-extrabold tracking-[0.18em] uppercase text-emerald-800">
              <ShieldCheck className="w-3.5 h-3.5"/> Canadian Government Resources
            </div>
            <h2 className="font-display mt-3 text-2xl sm:text-3xl md:text-4xl font-black tracking-tight text-slate-950 leading-[1.05]">
              Housing, landlord &amp; brokerage info — straight from the source
            </h2>
            <p className="mt-2 text-sm sm:text-base text-slate-700 leading-relaxed">
              Every province writes its own rules. We cross-reference every rule inside Central Rentals against the official government registers below — click through any card to read the source.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Housing */}
            <div className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all" data-testid="gov-card-housing">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center flex-none"><Home className="w-6 h-6"/></div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">Housing</div>
                  <h3 className="mt-1 font-display font-extrabold text-lg text-slate-900">Canada Mortgage and Housing Corporation (CMHC)</h3>
                  <p className="mt-2 text-sm text-slate-700 leading-relaxed">Federal housing agency. Rental market reports, affordable-housing programs, mortgage-loan insurance, and national vacancy data.</p>
                  <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
                    <a href="https://www.cmhc-schl.gc.ca/professionals/housing-markets-data-and-research" target="_blank" rel="noopener noreferrer" data-testid="gov-link-cmhc-rental" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200">Rental market data →</a>
                    <a href="https://www.cmhc-schl.gc.ca/professionals/project-funding-and-mortgage-financing/funding-programs" target="_blank" rel="noopener noreferrer" data-testid="gov-link-cmhc-funding" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200">Funding programs →</a>
                    <a href="https://www.cmhc-schl.gc.ca/" target="_blank" rel="noopener noreferrer" data-testid="gov-link-cmhc-home" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200">CMHC homepage →</a>
                  </div>
                </div>
              </div>
            </div>

            {/* Landlord */}
            <div className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all" data-testid="gov-card-landlord">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-sky-100 text-sky-800 flex items-center justify-center flex-none"><Building2 className="w-6 h-6"/></div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-sky-700">Landlords</div>
                  <h3 className="mt-1 font-display font-extrabold text-lg text-slate-900">Provincial residential-tenancy tribunals</h3>
                  <p className="mt-2 text-sm text-slate-700 leading-relaxed">Every province operates its own landlord-tenant board with hearings, forms, notices, and dispute rulings. Central Rentals maps to each authority.</p>
                  <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
                    <a href="https://tribunalsontario.ca/ltb/" target="_blank" rel="noopener noreferrer" data-testid="gov-link-ltb" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-sky-50 text-sky-800 hover:bg-sky-100 border border-sky-200">Ontario LTB →</a>
                    <a href="https://www.tal.gouv.qc.ca/en/" target="_blank" rel="noopener noreferrer" data-testid="gov-link-tal" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-sky-50 text-sky-800 hover:bg-sky-100 border border-sky-200">Quebec TAL →</a>
                    <a href="https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies" target="_blank" rel="noopener noreferrer" data-testid="gov-link-rtb-bc" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-sky-50 text-sky-800 hover:bg-sky-100 border border-sky-200">BC RTB →</a>
                    <a href="https://www.alberta.ca/rtdrs" target="_blank" rel="noopener noreferrer" data-testid="gov-link-rtdrs" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-sky-50 text-sky-800 hover:bg-sky-100 border border-sky-200">Alberta RTDRS →</a>
                  </div>
                </div>
              </div>
            </div>

            {/* Brokerage */}
            <div className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all" data-testid="gov-card-brokerage">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-violet-100 text-violet-800 flex items-center justify-center flex-none"><Briefcase className="w-6 h-6"/></div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-violet-700">Brokerages</div>
                  <h3 className="mt-1 font-display font-extrabold text-lg text-slate-900">Provincial real-estate councils & regulators</h3>
                  <p className="mt-2 text-sm text-slate-700 leading-relaxed">Licensing, trust-account rules, disciplinary rulings, and consumer-protection guidance for every registered brokerage in Canada.</p>
                  <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
                    <a href="https://www.reco.on.ca/" target="_blank" rel="noopener noreferrer" data-testid="gov-link-reco" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-violet-50 text-violet-800 hover:bg-violet-100 border border-violet-200">RECO (Ontario) →</a>
                    <a href="https://www.bcfsa.ca/industry-resources/real-estate-professional-resources" target="_blank" rel="noopener noreferrer" data-testid="gov-link-bcfsa" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-violet-50 text-violet-800 hover:bg-violet-100 border border-violet-200">BCFSA (BC) →</a>
                    <a href="https://www.recaquebec.ca/en" target="_blank" rel="noopener noreferrer" data-testid="gov-link-reca-qc" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-violet-50 text-violet-800 hover:bg-violet-100 border border-violet-200">OACIQ (Quebec) →</a>
                    <a href="https://www.reca.ca/" target="_blank" rel="noopener noreferrer" data-testid="gov-link-reca-ab" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-violet-50 text-violet-800 hover:bg-violet-100 border border-violet-200">RECA (Alberta) →</a>
                  </div>
                </div>
              </div>
            </div>

            {/* Property Manager */}
            <div className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all" data-testid="gov-card-pm">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center flex-none"><Users className="w-6 h-6"/></div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-amber-700">Property Managers</div>
                  <h3 className="mt-1 font-display font-extrabold text-lg text-slate-900">CRA, Statistics Canada & industry associations</h3>
                  <p className="mt-2 text-sm text-slate-700 leading-relaxed">Tax reporting (T776), CRA GST/HST guidance, housing-statistics feeds, and the two national trade bodies (CFAA + REIC) for licensed managers.</p>
                  <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
                    <a href="https://www.canada.ca/en/revenue-agency/services/forms-publications/forms/t776.html" target="_blank" rel="noopener noreferrer" data-testid="gov-link-cra-t776" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200">CRA T776 →</a>
                    <a href="https://www.statcan.gc.ca/en/subjects-start/housing" target="_blank" rel="noopener noreferrer" data-testid="gov-link-statcan" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200">Statistics Canada housing →</a>
                    <a href="https://www.cfaa-fcapi.org/" target="_blank" rel="noopener noreferrer" data-testid="gov-link-cfaa" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200">CFAA →</a>
                    <a href="https://www.reic.ca/" target="_blank" rel="noopener noreferrer" data-testid="gov-link-reic" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200">REIC →</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center text-xs text-slate-500">
            External links open in a new tab.
          </div>

          {/* Every-province link band — covers all 13 provinces + territories
              so no user leaves the page thinking their jurisdiction is missing.
              Links audited Feb 2026 against the current government portals. */}
          <div className="mt-10 rounded-2xl bg-white border border-slate-200 p-6" data-testid="gov-all-provinces">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">
              Residential tenancy authority — by province &amp; territory
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-sm">
              {[
                { p: "Ontario (LTB)",                url: "https://tribunalsontario.ca/ltb/" },
                { p: "Quebec (TAL)",                 url: "https://www.tal.gouv.qc.ca/en/" },
                { p: "British Columbia (RTB)",       url: "https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies" },
                { p: "Alberta (RTDRS)",              url: "https://www.alberta.ca/rtdrs" },
                { p: "Manitoba (RTB)",               url: "https://www.gov.mb.ca/cca/rtb/" },
                { p: "Saskatchewan (ORT)",           url: "https://www.saskatchewan.ca/residents/housing/renting-a-home" },
                { p: "Nova Scotia (Residential Tenancies)", url: "https://beta.novascotia.ca/programs-and-services/residential-tenancies" },
                { p: "New Brunswick (RT Tribunal)",  url: "https://www2.snb.ca/content/snb/en/sites/rentalsman.html" },
                { p: "PEI (IRAC)",                   url: "https://www.irac.pe.ca/rental/" },
                { p: "Newfoundland (RT Section)",    url: "https://www.gov.nl.ca/dgsnl/consumer-affairs/landlord/" },
                { p: "Yukon (RT Office)",            url: "https://yukon.ca/en/housing-and-property/renting/find-help-tenants-and-landlords" },
                { p: "NWT (RT Office)",              url: "https://www.justice.gov.nt.ca/en/rental-office/" },
                { p: "Nunavut (Residential Tenancies)", url: "https://www.gov.nu.ca/justice/information/rental-office" },
              ].map((x) => (
                <a key={x.p} href={x.url} target="_blank" rel="noopener noreferrer"
                  data-testid={`gov-prov-${x.p.split(" ")[0].toLowerCase()}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-50 hover:bg-emerald-50 hover:text-emerald-800 border border-slate-200 text-slate-700 font-medium transition-colors">
                  <span className="truncate">{x.p}</span>
                  <span className="text-emerald-600">↗</span>
                </a>
              ))}
            </div>
          </div>

          {/* Federal & national bodies band */}
          <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-6" data-testid="gov-federal">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">
              Federal &amp; national resources
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-sm">
              {[
                { n: "CRA — T776 rental income",  url: "https://www.canada.ca/en/revenue-agency/services/forms-publications/forms/t776.html" },
                { n: "CRA — NR4 non-resident",    url: "https://www.canada.ca/en/revenue-agency/services/forms-publications/forms/nr4.html" },
                { n: "PIPEDA (Privacy Act)",      url: "https://www.priv.gc.ca/en/privacy-topics/privacy-laws-in-canada/the-personal-information-protection-and-electronic-documents-act-pipeda/" },
                { n: "Statistics Canada — Housing", url: "https://www.statcan.gc.ca/en/subjects-start/housing" },
                { n: "Government of Canada — Housing", url: "https://www.canada.ca/en/services/housing.html" },
                { n: "CFAA (Landlord federation)", url: "https://cfaa-fcapi.org/" },
                { n: "REIC (Real Estate Institute)", url: "https://www.reic.ca/" },
              ].map((x) => (
                <a key={x.n} href={x.url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-50 hover:bg-emerald-50 hover:text-emerald-800 border border-slate-200 text-slate-700 font-medium transition-colors">
                  <span className="truncate">{x.n}</span>
                  <span className="text-emerald-600">↗</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GLOW LOGIN CTA — light pastel aurora variant */}
      <section className="py-6 md:py-8 relative overflow-hidden bg-gradient-to-br from-sky-50 via-white to-emerald-50">
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(700px 360px at 50% 50%, rgba(56,189,248,0.18), transparent 70%)"
        }}/>
        <div className="relative max-w-3xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter text-slate-900">
            Ready when you are.
          </h2>
          <p className="mt-3 text-slate-600 text-base md:text-lg">Create a free account or sign in — your dashboard is one tap away.</p>

          {/* SIGN UP — primary CTAs */}
          <div className="mt-9 flex flex-col sm:flex-row justify-center items-center gap-4">
            <Link to="/signup?role=landlord&plan=professional" data-testid="glow-signup-landlord"
              className="glow-button inline-flex items-center justify-center gap-2 px-9 py-4 rounded-full bg-emerald-600 text-white font-bold text-base shadow-[0_0_40px_rgba(16,185,129,0.45)] hover:shadow-[0_0_60px_rgba(16,185,129,0.7)] hover:bg-emerald-700 transition-all w-full sm:w-auto">
              <ShieldCheck className="w-5 h-5"/> Sign up — Landlord
            </Link>
            <Link to="/signup?role=tenant" data-testid="glow-signup-tenant"
              className="glow-button inline-flex items-center justify-center gap-2 px-9 py-4 rounded-full bg-white text-slate-900 font-bold text-base border border-slate-200 shadow-[0_0_30px_rgba(125,211,252,0.35)] hover:shadow-[0_0_50px_rgba(125,211,252,0.55)] hover:bg-sky-50 transition-all w-full sm:w-auto">
              Sign up — Tenant
            </Link>
          </div>

          <div className="mt-5 text-xs text-slate-500">
            Already have an account?{" "}
            <Link to="/login/landlord" data-testid="glow-text-login-landlord" className="text-emerald-700 hover:text-emerald-800 font-semibold underline-offset-4 hover:underline">
              Landlord sign-in
            </Link>
            {" · "}
            <Link to="/login/tenant" data-testid="glow-text-login-tenant" className="text-emerald-700 hover:text-emerald-800 font-semibold underline-offset-4 hover:underline">
              Tenant sign-in
            </Link>
          </div>

          <div className="mt-3 text-xs text-slate-500">Trusted login · TLS 1.3 · No card required to start</div>
        </div>
      </section>

      {/* Free landlord tools — 10 working micro-tools, no signup needed. */}
      <FreeToolsGrid />

      {/* Resources hub — 18 deep-linked resources (6 per ICP) targeting
          property managers, landlords, and tenants. Every link is
          verified live so nothing dead-ends. */}
      <ResourcesHub />

      <footer className="border-t border-slate-200 bg-white text-slate-700">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-12 pb-0 space-y-10">
          {/* Newsletter — top of footer for max conversion */}
          <div className="grid md:grid-cols-12 gap-8 md:gap-10 items-center pb-2 border-b border-slate-100">
            <div className="md:col-span-7">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">{t("footer.field_notes", "Field notes")}</div>
              <h3 className="font-display text-2xl md:text-3xl font-extrabold tracking-tighter text-slate-900 mt-2">{t("footer.newsletter_title", "Field notes for Canadian landlords.")}</h3>
              <p className="text-sm text-slate-600 mt-2 max-w-md">{t("footer.newsletter_desc", "Plain-language reads on CRA, provincial law, and the operational stuff that actually moves rent. Written by landlords, for landlords. CASL-compliant, one-click unsubscribe.")}</p>
            </div>
            <div className="md:col-span-5">
              <NewsletterSignup source="landing" compact />
            </div>
          </div>

          {/* Brand · About · Links */}
          <div className="grid md:grid-cols-12 gap-10 md:gap-8">
            <div className="md:col-span-5">
              <div className="select-none inline-flex flex-col items-start whitespace-nowrap">
                <span className="font-display text-2xl sm:text-3xl md:text-4xl font-black tracking-tight text-slate-900 leading-none">
                  Central Rentals Canada
                </span>
                <span className="mt-2 font-display text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em] text-emerald-700 leading-none">
                  PM Software
                </span>
              </div>
              <p className="mt-5 text-sm text-slate-800 font-semibold leading-relaxed max-w-md">
                {t("footer.brand_tagline_1", "Central Rentals Canada is a registered Canadian company serving landlords, tenants, brokerages, and property managers across all 13 Canadian provinces and territories.")}
              </p>
              <p className="mt-3 text-sm text-slate-700 leading-relaxed max-w-md">
                {t("footer.brand_tagline_2", "We built one operating system for Canadian property management: bilingual leases (English + French), province-aware notices (LTB, TAL, RTB, RTDRS), Rent tracking with Klarna Pay-in-4, trust-account bookkeeping ready for the CRA T776, and PIPEDA-compliant tenant screening — all under one roof, on one subscription.")}
              </p>
              <p className="mt-3 text-sm text-slate-700 leading-relaxed max-w-md">
                {t("footer.brand_tagline_3", "Our commitment: client success, genuine relationship-building, and shared values with the owners and tenants we serve. Central Rentals aggressively supports our operators — nights, weekends, and every language your community speaks.")}
              </p>
              <div className="mt-5 flex items-center gap-2 flex-wrap">
                <a href="https://www.linkedin.com/company/central-rentals-edmonton/" target="_blank" rel="noreferrer" data-testid="footer-linkedin"
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-900 text-sm font-semibold transition-colors">
                  <Linkedin className="w-4 h-4"/> LinkedIn
                </a>
                <a href="https://www.trustedsite.com/" target="_blank" rel="noreferrer" data-testid="footer-trustedsite"
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-900 text-xs font-semibold">
                  <ShieldCheck className="w-4 h-4 text-emerald-700"/> {t("footer.trustedsite", "Secured with TrustedSite")}
                </a>
              </div>
            </div>

            <div className="md:col-span-4">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">{t("footer.accessibility", "Accessibility")}</div>
              <p className="mt-3 text-sm text-slate-800 leading-relaxed font-semibold">
                {t("footer.accessibility_text", "CRC Inc. guarantees equal access for individuals with disabilities in full compliance with the Accessible Canada Act and related regulations and standards.")}
              </p>
              <div className="mt-5 text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">{t("footer.contact", "Contact")}</div>
              <div className="mt-3 flex items-center gap-3 text-sm">
                <a
                  href="tel:+17804052498"
                  data-testid="footer-phone"
                  aria-label="Call Central Rentals — 780-405-2498"
                  title="Call Central Rentals · 780-405-2498"
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200 transition-colors"
                >
                  <Phone className="w-4 h-4"/>
                </a>
                <a href="mailto:contact@centralrentals.ca" className="text-slate-800 hover:text-slate-950 font-semibold">contact@centralrentals.ca</a>
              </div>
            </div>

            <div className="md:col-span-3">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">{t("footer.company", "Company")}</div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-1 gap-x-4 gap-y-2.5 text-sm">
                <Link to="/about" data-testid="footer-about" className="text-slate-700 hover:text-slate-900 font-medium">{t("footer.about", "Who we are")}</Link>
                <Link to="/careers" data-testid="footer-careers" className="text-slate-700 hover:text-slate-900 font-medium">{t("footer.careers", "Careers")}</Link>
                <Link to="/diversity" data-testid="footer-diversity" className="text-slate-700 hover:text-slate-900 font-medium">{t("footer.diversity", "Diversity & Inclusion")}</Link>
                <Link to="/investors" data-testid="footer-investors" className="text-slate-700 hover:text-slate-900 font-medium">{t("footer.investors", "Investor Relations")}</Link>
                <Link to="/blog" data-testid="footer-blog" className="text-slate-700 hover:text-slate-900 font-medium">{t("footer.blog", "Blog")}</Link>
                <Link to="/university" data-testid="footer-university" className="text-slate-700 hover:text-slate-900 font-medium">{t("footer.university", "Landlord University")}</Link>
                <Link to="/laws/on" data-testid="footer-laws" className="text-slate-700 hover:text-slate-900 font-medium">{t("footer.laws", "Landlord laws by province")}</Link>
                <Link to="/report/landlord-2026-canada" data-testid="footer-report" className="text-slate-700 hover:text-slate-900 font-medium">{t("footer.report", "2026 Landlord Report")}</Link>
                <Link to="/changelog" data-testid="footer-changelog" className="text-slate-700 hover:text-slate-900 font-medium">{t("footer.changelog", "What's new")}</Link>
                <Link to="/privacy" data-testid="footer-privacy" className="text-slate-700 hover:text-slate-900 font-medium">{t("footer.privacy", "Privacy")}</Link>
                <Link to="/terms" data-testid="footer-terms" className="text-slate-700 hover:text-slate-900 font-medium">{t("footer.terms", "Terms")}</Link>
                <a href="https://maps.app.goo.gl/3R94HfmbLnbNgUvk9?g_st=ic" target="_blank" rel="noopener noreferrer" data-testid="footer-gbp" className="text-slate-700 hover:text-slate-900 font-medium">Find us on Google</a>
              </div>
            </div>
          </div>

          {/* Software by province */}
          <div className="pt-8 border-t border-slate-200" data-testid="footer-software-provinces">
            <div className="mb-5">
              <Link
                to="/property-management-software"
                data-testid="footer-hub-pmsc"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100 text-sm font-semibold hover:bg-emerald-100 transition-colors"
              >
                Property management software Canada · Central Rentals
              </Link>
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">{t("footer.software_by_prov", "Property management software by province")}</div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-x-4 gap-y-2 text-sm">
              <Link to="/landlord-software/ontario" className="text-slate-700 hover:text-emerald-700 font-medium">Ontario</Link>
              <Link to="/landlord-software/british-columbia" className="text-slate-700 hover:text-emerald-700 font-medium">British Columbia</Link>
              <Link to="/landlord-software/alberta" className="text-slate-700 hover:text-emerald-700 font-medium">Alberta</Link>
              <Link to="/landlord-software/quebec" className="text-slate-700 hover:text-emerald-700 font-medium">Quebec</Link>
              <Link to="/landlord-software/manitoba" className="text-slate-700 hover:text-emerald-700 font-medium">Manitoba</Link>
              <Link to="/landlord-software/saskatchewan" className="text-slate-700 hover:text-emerald-700 font-medium">Saskatchewan</Link>
              <Link to="/landlord-software/nova-scotia" className="text-slate-700 hover:text-emerald-700 font-medium">Nova Scotia</Link>
              <Link to="/landlord-software/new-brunswick" className="text-slate-700 hover:text-emerald-700 font-medium">New Brunswick</Link>
              <Link to="/landlord-software/prince-edward-island" className="text-slate-700 hover:text-emerald-700 font-medium">PEI</Link>
              <Link to="/landlord-software/newfoundland-and-labrador" className="text-slate-700 hover:text-emerald-700 font-medium">Newfoundland &amp; Labrador</Link>
              <Link to="/landlord-software/yukon" className="text-slate-700 hover:text-emerald-700 font-medium">Yukon</Link>
              <Link to="/landlord-software/northwest-territories" className="text-slate-700 hover:text-emerald-700 font-medium">Northwest Territories</Link>
              <Link to="/landlord-software/nunavut" className="text-slate-700 hover:text-emerald-700 font-medium">Nunavut</Link>
            </div>
          </div>

          {/* Software by city — critical for Google to discover the
              per-city landing pages. Without these internal links the
              city pages become crawl-orphaned and never rank. */}
          <div className="pt-8 border-t border-slate-200" data-testid="footer-software-cities">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Property management software by city</div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-2 text-sm">
              {[
                { s: "toronto",       n: "Toronto" },
                { s: "vancouver",     n: "Vancouver" },
                { s: "montreal",      n: "Montreal" },
                { s: "calgary",       n: "Calgary" },
                { s: "edmonton",      n: "Edmonton" },
                { s: "ottawa",        n: "Ottawa" },
                { s: "winnipeg",      n: "Winnipeg" },
                { s: "halifax",       n: "Halifax" },
                { s: "quebec-city",   n: "Quebec City" },
                { s: "hamilton",      n: "Hamilton" },
                { s: "kitchener",     n: "Kitchener" },
                { s: "london",        n: "London" },
                { s: "mississauga",   n: "Mississauga" },
                { s: "brampton",      n: "Brampton" },
                { s: "surrey",        n: "Surrey" },
                { s: "burnaby",       n: "Burnaby" },
                { s: "victoria",      n: "Victoria" },
                { s: "saskatoon",     n: "Saskatoon" },
                { s: "regina",        n: "Regina" },
                { s: "laval",         n: "Laval" },
                { s: "gatineau",      n: "Gatineau" },
                { s: "moncton",       n: "Moncton" },
                { s: "st-johns",      n: "St. John's" },
                { s: "charlottetown", n: "Charlottetown" },
              ].map((c) => (
                <Link key={c.s} to={`/property-management-software/${c.s}`}
                  data-testid={`footer-city-${c.s}`}
                  className="text-slate-700 hover:text-emerald-700 font-medium">
                  {c.n}
                </Link>
              ))}
            </div>
          </div>

          {/* Compare to competitors — internal-link block. High-intent
              buyer SEO ("buildium alternative canada" etc). Six links
              into footer of every page so PageRank flows in + Googlebot
              discovers + every visitor sees the comparison cluster. */}
          <div className="pt-8 border-t border-slate-200" data-testid="footer-compare">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Compare Central Rentals</div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-x-4 gap-y-2 text-sm">
              <Link to="/vs/buildium" className="text-slate-700 hover:text-emerald-700 font-medium">vs Buildium</Link>
              <Link to="/vs/appfolio" className="text-slate-700 hover:text-emerald-700 font-medium">vs AppFolio</Link>
              <Link to="/vs/yardi-breeze" className="text-slate-700 hover:text-emerald-700 font-medium">vs Yardi Breeze</Link>
              <Link to="/vs/rentsync" className="text-slate-700 hover:text-emerald-700 font-medium">vs Rentsync</Link>
              <Link to="/vs/tenantcloud" className="text-slate-700 hover:text-emerald-700 font-medium">vs TenantCloud</Link>
              <Link to="/vs/avail" className="text-slate-700 hover:text-emerald-700 font-medium">vs Avail</Link>
            </div>
          </div>

          {/* Partners & Affiliates — moved from the top header to the
              bottom of the homepage. Surfaces vendor + affiliate + brokerage
              partnership flows so the top navigation stays focused on
              product / pricing / signup. */}
          <div className="pt-8 border-t border-slate-200" data-testid="footer-partnerships">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Partners &amp; Affiliates</div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-x-4 gap-y-2.5 text-sm">
              <Link to="/partners" data-testid="footer-affiliate-apply" className="text-slate-700 hover:text-emerald-700 font-medium">Become an affiliate</Link>
              <Link to="/partners" data-testid="footer-vendor-apply" className="text-slate-700 hover:text-emerald-700 font-medium">Apply as a vendor</Link>
              <Link to="/partners" data-testid="footer-brokerages" className="text-slate-700 hover:text-emerald-700 font-medium">For brokerages</Link>
              <Link to="/migration" data-testid="footer-migration" className="text-slate-700 hover:text-emerald-700 font-medium">Zero-Effort Migration</Link>
              <Link to="/alternatives/appfolio" data-testid="footer-appfolio-alt" className="text-slate-700 hover:text-emerald-700 font-medium">AppFolio alternatives</Link>
            </div>
          </div>

          {/* Trust badges */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 pt-8 border-t border-slate-200" data-testid="footer-trust-badges">
            <Badge icon={ShieldCheck} title="Bank-level encryption" sub="HTTPS · TLS 1.3"/>
            <Badge icon={CreditCard} title="PCI-DSS via Stripe" sub="We never see your card"/>
            <Badge icon={MapPin} title="Canadian-owned" sub="Data hosted in Canada"/>
            <Badge icon={Users} title="PIPEDA-aligned" sub="Privacy first"/>
          </div>

          {/* Bottom bar — BoldTrail-style dark navy band with centered
              copyright + piped policy links. Overflows the padded footer
              container via negative margins so the dark band spans the
              full viewport width AND touches the very bottom of the
              page (no residual white gap under the dark band). */}
          <div className="-mx-5 md:-mx-8 -mb-0 mt-6 bg-slate-800 text-white" data-testid="footer-bottom-bar">
            <div className="max-w-4xl mx-auto px-6 py-8 md:py-10 text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-white" data-testid="footer-brand-title">
                Central Rentals Canada
              </div>
              <div className="mt-8 footer-piped-links text-white/90" data-testid="footer-policy-links">
                <Link to="/legal/privacy" data-testid="footer-policy-privacy" className="hover:text-emerald-300 transition-colors">Privacy Policy</Link>
                <Link to="/legal/terms" data-testid="footer-policy-terms" className="hover:text-emerald-300 transition-colors">Terms of Service</Link>
                <Link to="/legal/screening" data-testid="footer-policy-screening" className="hover:text-emerald-300 transition-colors">Screening Disclosure</Link>
                <Link to="/legal/accessibility" data-testid="footer-policy-a11y" className="hover:text-emerald-300 transition-colors">Accessibility</Link>
              </div>
              <div className="mt-6 text-sm sm:text-base font-bold text-white/80 flex items-center justify-center gap-2">
                <Users className="w-4 h-4"/> Made in Canada · 2016–{new Date().getFullYear()}
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Mark — public rule-based helper. Answers only from what's built
          into Central Rentals; no LLM calls, no web search. */}
      <SmartChatBubble />

      {/* Exit-intent lead magnet — captures email on intent-to-leave */}
      <SmartLeadMagnet />
    </div>
  );
}

function BlogTeaser_REMOVED() { return null; }
// Component moved to /components/landing/BlogTeaser.jsx

function Stat_REMOVED() { return null; }
function Badge_REMOVED() { return null; }
// Stat + Badge moved to /components/landing/shared.jsx
